/**
 * Null-safe sorting for react-table columns.
 *
 * react-table's built-in 'basic' sort type compares with `>`. When one side is
 * undefined or null (a client with no pallet adjustment yet) that comparison
 * answers the same way in both directions, Array.prototype.sort gets
 * inconsistent answers, and the whole list comes out scrambled
 * (parrot-media/portal/main#315).
 *
 * The comparators here treat undefined, null, '' and unparseable values as
 * blank, keep blanks at the bottom in both directions, and compare real values
 * by kind: 'date' (chronological), 'number' or 'text' (case-insensitive).
 */

/**
 * Check for a value with nothing to sort by
 *
 * @param value
 * @return {boolean}
 */
export const isBlankValue = (value) => (value === undefined || value === null || value === '');

// Normalisers: convert a raw cell value into a comparable primitive, or null when blank
const NORMALISERS = {
    date: (value) => {
        if (isBlankValue(value)) {
            return null;
        }
        const time = new Date(value).getTime();
        return isNaN(time) ? null : time;
    },
    number: (value) => {
        if (isBlankValue(value)) {
            return null;
        }
        const number = Number(value);
        return isNaN(number) ? null : number;
    },
    text: (value) => (isBlankValue(value)) ? null : String(value).toLowerCase()
};

// Comparators for two normalised, non-blank values of the same kind
const COMPARATORS = {
    date: (a, b) => a - b,
    number: (a, b) => a - b,
    text: (a, b) => a.localeCompare(b)
};

export const SORT_KINDS = Object.keys(NORMALISERS);

const assertKind = (kind) => {
    if (!NORMALISERS[kind]) {
        throw new Error(`Unknown sort kind '${kind}'; expected one of: ${SORT_KINDS.join(', ')}`);
    }
};

/**
 * Compare two normalised values, blanks (null) last in both directions.
 *
 * react-table negates the result for a descending sort, so a blank has to come
 * out "smaller" when desc is set for it to still land at the bottom.
 */
const compareBlanksLast = (a, b, compare, desc) => {
    if (a === null && b === null) {
        return 0;
    }
    if (a === null) {
        return (desc) ? -1 : 1;
    }
    if (b === null) {
        return (desc) ? 1 : -1;
    }
    return compare(a, b);
};

/**
 * Build a react-table sortType for a column of the given kind.
 *
 * react-table calls a sortType as (rowA, rowB, columnId, desc) and negates the
 * result itself when desc is set.
 *
 * @param {'date'|'number'|'text'} kind
 * @return {function(*, *, string, boolean): number}
 */
export const blanksLastSortType = (kind) => {
    assertKind(kind);
    const normalise = NORMALISERS[kind];
    const compare = COMPARATORS[kind];
    return (rowA, rowB, columnId, desc) => compareBlanksLast(
        normalise(rowA.values[columnId]),
        normalise(rowB.values[columnId]),
        compare,
        desc
    );
};

/**
 * Column definition fragment: spread into a react-table column to sort it
 * blanks-last by kind, e.g. `{accessor: 'modified', ...blanksLastSort('date')}`.
 *
 * @param {'date'|'number'|'text'} kind
 * @return {{sortType: function}}
 */
export const blanksLastSort = (kind) => ({
    sortType: blanksLastSortType(kind)
});
